import { Add } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ValidationErrors } from 'fluentvalidation-ts';
import React, { FC, useEffect, useState } from 'react';
import { valueToText } from '../../../../Common/CommonMethods';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import {
  ThemedCopyIcon,
  ThemedDeleteIcon,
  ThemedEditIcon,
} from '../../../../Common/Components/Icons';
import { Loader } from '../../../../Common/Components/Loader';
import { useFarmStyles } from '../../../../Common/Styles';
import { Product, Subscription } from '../../../Common/CommonModel';
import { commonService } from '../../../Common/CommonService';
import { CardWrapper } from '../../Common/CommonComponents';
import { DialogOptions } from '../../Monitoring/Model/MonitoringModel';
import { validDMS, validDTUs } from '../Model/SqlServerData';
import {
  FailoverGroup,
  OperationType,
  SqlServer,
} from '../Model/SqlServerModel';
import { sqlServerService } from '../SqlServerService';
import { SqlServerValidator } from '../Validation/SqlServerValidator';
import { FailoverGroupForm } from './FailoverGroupForm';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toastForAdminPasswordCopy } from '../Common/notification';

interface SqlServerFormProps {
  onFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sqlserver: SqlServer;
  setSqlServer: (value: React.SetStateAction<SqlServer>) => void;
  errors: ValidationErrors<SqlServer>;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors<SqlServer>>>;
  sqlServerOperation: OperationType;
}

const SqlServerForm: FC<SqlServerFormProps> = (props) => {
  const { classes } = useFarmStyles();
  const [regions, setRegions] = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [subscriptions, setSuscriptions] = useState<Subscription[]>([]);
  const [failovergroup, setFailovergroup] = useState<FailoverGroup>({
    Name: '',
    ServerName: '',
    Region: '',
    AdminPassword: '',
    UserName: '',
    EnableElasticPool: true,
    DataMaxSize: 50,
    EDtu: 50,
    PerDbDtu: 10,
  });
  const [loading, setLoading] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const {
    onFieldChange,
    sqlserver,
    setSqlServer,
    errors,
    setErrors,
    sqlServerOperation,
  } = props;
  const [failoverOperation, setFailoverOperation] = useState<OperationType>(
    OperationType.Create,
  );
  const [sqlservers, setSqlServers] = useState<SqlServer[]>([]);
  const sqlServerValidator = new SqlServerValidator();

  const validate = () => {
    const sqlserverErrors = sqlServerValidator.validate(sqlserver);
    if (sqlserver.ServerName && sqlServerOperation === OperationType.Create) {
      const exists = checkServerExists(sqlserver.ServerName);
      if (exists) {
        sqlserverErrors.ServerName = 'This server already exists';
      }
    }
    setErrors(sqlserverErrors);
  };

  function checkServerExists(servername: string) {
    return sqlservers.find((server) => server.ServerName === servername)
      ? true
      : false;
  }

  const validateSqlServer = () => {
    validate();
  };

  const handleSliderChange =
    (field: string) => (event: Event, value: number | number[]) => {
      setSqlServer((prev) => ({ ...prev, [field]: value as number }));
    };

  const Init = async () => {
    setLoading((prevLoading) => prevLoading + 1);
    await loadAzureRegions();
    await loadSubscriptions();
    await loadProducts();
    await loadSqlServers();
    await setLoading((prevLoading) => prevLoading - 1);
  };

  const handleDialogue = (value: boolean) => {
    setOpen(value);
    reInitializeDialog();
    setFailoverOperation(OperationType.Create);
  };

  const handleDelete = (group: string) => {
    setSqlServer((prev) => ({
      ...prev,
      FailoverGroups: prev.FailoverGroups.filter(
        (failovergroup) => failovergroup.Name !== group,
      ),
    }));
  };

  const handleEdit = (group: string) => {
    setFailoverOperation(OperationType.Edit);
    setOpen(true);
    setFailovergroup(
      sqlserver.FailoverGroups.find(
        (failovergroup) => failovergroup.Name === group,
      ) as FailoverGroup,
    );
  };

  const reInitializeDialog = () => {
    setFailovergroup({
      Name: '',
      ServerName: '',
      Region: '',
      AdminPassword: sqlserver.AdminPassword,
      UserName: '',
      EnableElasticPool: true,
      DataMaxSize: 50,
      EDtu: 50,
      PerDbDtu: 10,
    });
  };

  const handleAddFailovergroup = () => {
    setSqlServer((prev) => ({
      ...prev,
      FailoverGroups: [...prev.FailoverGroups, failovergroup],
    }));
  };

  const handleEditFailovergroup = () => {
    setSqlServer((prev) => ({
      ...prev,
      FailoverGroups: prev.FailoverGroups.map((group) =>
        group.ServerName === failovergroup.ServerName ? failovergroup : group,
      ),
    }));
  };

  const handleDialogueOptions = (option: DialogOptions) => {
    if (
      DialogOptions.Ok === option &&
      failoverOperation === OperationType.Create
    ) {
      handleAddFailovergroup();
    } else if (
      DialogOptions.Ok === option &&
      failoverOperation === OperationType.Edit
    ) {
      handleEditFailovergroup();
    }
    reInitializeDialog();
    handleDialogue(false);
  };

  const loadAzureRegions = async () => {
    const azureRegions = await commonService.getAzureRegions();
    setRegions(azureRegions.toSorted((a, b) => a.localeCompare(b)));
  };

  const loadSubscriptions = async () => {
    const subscriptions = await commonService.getSubscriptions();
    setSuscriptions(subscriptions.Items);
  };

  const loadSqlServers = async () => {
    const servers = await sqlServerService.getSqlServers();
    setSqlServers(servers.Items);
  };

  const loadProducts = async () => {
    const products = await commonService.getProducts();
    setProducts(products.Items);
  };

  const canShowFailoverTable = () => {
    return sqlserver.FailoverGroups && sqlserver.FailoverGroups.length > 0;
  };

  useEffect(() => {
    Init();
    validate();
  }, []);

  useEffect(() => {
    validate();
  }, [sqlserver]);

  return (
    <>
      {loading > 0 ? (
        <Loader loading={loading > 0} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <CardWrapper>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Basic Informations
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Subdomain</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    error={!!errors.Subscription}
                    helperText={errors.Subscription}
                    value={sqlserver.Subscription}
                    placeholder="Subscription"
                    name="Subscription"
                    onChange={onFieldChange}
                    onBlur={validate}
                  >
                    {subscriptions.map((subscription) => (
                      <MenuItem
                        key={subscription.Name}
                        value={subscription.Name}
                      >
                        {subscription.Name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Resource Group</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    error={!!errors.ResourceGroup}
                    helperText={errors.ResourceGroup}
                    value={sqlserver.ResourceGroup}
                    placeholder="Resource Group"
                    name="ResourceGroup"
                    onChange={onFieldChange}
                    onBlur={validate}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Server Name</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    error={!!errors.ServerName}
                    helperText={errors.ServerName}
                    value={sqlserver.ServerName}
                    placeholder="Server Name"
                    name="ServerName"
                    onChange={(e) => {
                      onFieldChange(e as React.ChangeEvent<HTMLInputElement>);
                      validateSqlServer();
                    }}
                    onBlur={validateSqlServer}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">User Name</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    error={!!errors.UserName}
                    helperText={errors.UserName}
                    value={sqlserver.UserName}
                    placeholder="User Name"
                    name="UserName"
                    onChange={onFieldChange}
                    onBlur={validate}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Admin Password</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    disabled={true}
                    type={'password'}
                    error={!!errors.AdminPassword}
                    helperText={errors.AdminPassword}
                    value={sqlserver.AdminPassword}
                    placeholder="Admin Password"
                    name="AdminPassword"
                    onChange={onFieldChange}
                    onBlur={validate}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CopyToClipboard
                            text={sqlserver.AdminPassword}
                            onCopy={toastForAdminPasswordCopy}
                          >
                            <IconButton size="small">
                              <ThemedCopyIcon fontSize="small" />
                            </IconButton>
                          </CopyToClipboard>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Location/Region</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    error={!!errors.Region}
                    helperText={errors.Region}
                    value={sqlserver.Region}
                    placeholder="Region"
                    name="Region"
                    onChange={onFieldChange}
                    onBlur={validate}
                    select
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 350,
                          },
                        },
                      },
                    }}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Product</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    error={!!errors.ProductID}
                    helperText={errors.ProductID}
                    value={sqlserver.ProductID}
                    placeholder="Product"
                    name="ProductID"
                    onChange={onFieldChange}
                    onBlur={validate}
                    select
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 350,
                          },
                        },
                      },
                    }}
                  >
                    {products.map((product) => (
                      <MenuItem key={product.Id} value={product.Id}>
                        {product.Id}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
            </CardWrapper>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <CardWrapper>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Aditional Configurations
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Enable Elastic Pool</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Checkbox
                    sx={{ float: 'left' }}
                    id="Enable Elastic Pool"
                    value={sqlserver.EnableElasticPool ?? false}
                    checked={sqlserver.EnableElasticPool ?? false}
                    name="EnableElasticPool"
                    onChange={(e) => onFieldChange(e)}
                    onBlur={validate}
                  />
                </Grid>
              </Grid>
              {sqlserver.EnableElasticPool && (
                <>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">eDTUs</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Slider
                          aria-label="Small steps"
                          defaultValue={sqlserver.EDtu}
                          getAriaValueText={valueToText}
                          step={null}
                          marks={validDTUs}
                          min={50}
                          max={3000}
                          onChange={handleSliderChange('EDtu')}
                          valueLabelDisplay="auto"
                        />
                        <CustomTextField
                          name="edtu"
                          error={!!errors.EDtu}
                          helperText={errors.EDtu}
                          value={sqlserver.EDtu}
                          onBlur={validate}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        Data max size (GB)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Slider
                          aria-label="Small steps"
                          defaultValue={sqlserver.DataMaxSize}
                          getAriaValueText={valueToText}
                          marks={validDMS}
                          step={null}
                          min={50}
                          max={3000}
                          onChange={handleSliderChange('DataMaxSize')}
                          valueLabelDisplay="auto"
                        />
                        <CustomTextField
                          name="DataMaxSize"
                          error={!!errors.DataMaxSize}
                          helperText={errors.DataMaxSize}
                          value={sqlserver.DataMaxSize}
                          onBlur={validate}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Per database DTUs</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Slider
                          aria-label="Small steps"
                          defaultValue={sqlserver.PerDbDtu}
                          getAriaValueText={valueToText}
                          step={null}
                          min={0}
                          max={sqlserver.EDtu}
                          marks={validDTUs}
                          onChange={handleSliderChange('PerDbDtu')}
                          valueLabelDisplay="auto"
                        />
                        <CustomTextField
                          name="PerDbDtu"
                          error={!!errors.PerDbDtu}
                          helperText={errors.PerDbDtu}
                          value={sqlserver.PerDbDtu}
                          onBlur={validate}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </CardWrapper>
            <FailoverGroupForm
              sqlserver={sqlserver}
              failovergroup={failovergroup}
              setFailovergroup={setFailovergroup}
              regions={regions}
              open={open}
              handleDialogueOptions={handleDialogueOptions}
              operationType={failoverOperation}
            />

            <CardWrapper sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Configure Failover
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <div className={classes.toolsHolder}>
                <CustomButton
                  disabled={sqlserver.FailoverGroups.length === 1}
                  onClick={() => handleDialogue(true)}
                  startIcon={<Add />}
                  label="Add"
                ></CustomButton>
              </div>
              {canShowFailoverTable() && (
                <div className={classes.spanContainer}>
                  <TableContainer style={{ maxHeight: 450 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Server</TableCell>
                          <TableCell>Region</TableCell>
                          <TableCell>Delete</TableCell>
                          <TableCell>Edit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sqlserver.FailoverGroups.map((group) => {
                          return (
                            <TableRow>
                              <TableCell>{group.Name}</TableCell>
                              <TableCell>{group.ServerName}</TableCell>
                              <TableCell>{group.Region}</TableCell>
                              <TableCell>
                                <IconButton
                                  aria-label="delete"
                                  color="primary"
                                  onClick={() => handleDelete(group.Name)}
                                >
                                  <ThemedDeleteIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  aria-label="delete"
                                  color="primary"
                                  onClick={() => handleEdit(group.Name)}
                                >
                                  <ThemedEditIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </CardWrapper>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SqlServerForm;
